import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Drawer, Input, Popconfirm, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useAutoFocusFirstField } from "hooks/useAutoFocusFirstField";
import {
  type BidAssociation,
  type Classifications,
  type Commitment,
  type DsmScopeItem,
  type ScopeItemInput,
  type ScopeStatus
} from "design-scope";
import ClassificationPicker from "./classification-picker";
import ScopeDesignPackageCommitmentsEditor, {
  isDesignPackageCommitmentsReady,
  pendingDesignPackageCommitments
} from "./scope-design-package-commitments-editor";
import ScopeBidPackagesCardsEditor, {
  pendingBidAssociations
} from "./scope-bid-packages-cards-editor";
import { blankCommitment, Field, Section } from "./scope-item-field";
import ScopeResponsibilitySelect from "./scope-responsibility-select";
import { ErrorMessages } from "constants/index";
import type { ProjectEntities } from "../hooks/use-project-entities";
import type { LevelOfDetailOption } from "../hooks/use-level-of-detail";
import {
  findResponsibilityOption,
  type AddDesignScopeResponsibility,
  type DesignScopeResponsibilityOption
} from "../shared/design-scope-responsibilities";
import "./scope-item.scss";

export default function ScopeItemModal(props: {
  open: boolean;
  initial?: DsmScopeItem;
  seed?: {
    classification?: Classifications;
    commitments?: Commitment[];
    bids?: BidAssociation[];
  };
  entities: ProjectEntities;
  lodOptions: LevelOfDetailOption[];
  lodOptionsLoading?: boolean;
  /** Project responsibility catalog. Omit to keep the built-in list. */
  responsibilities?: DesignScopeResponsibilityOption[];
  responsibilitiesLoading?: boolean;
  onAddResponsibility?: AddDesignScopeResponsibility;
  /** All log rows used for immediate title uniqueness (not the filtered grid). */
  existingTitles?: Array<{ id: string; title: string | null }>;
  onSubmit: (input: ScopeItemInput) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  onClose: () => void;
}) {
  const {
    open,
    initial,
    seed,
    entities,
    existingTitles = [],
    lodOptions,
    lodOptionsLoading,
    responsibilities,
    responsibilitiesLoading,
    onAddResponsibility,
    onSubmit,
    onDelete,
    onClose
  } = props;
  const formContainerRef = useRef<HTMLDivElement | null>(null);
  useAutoFocusFirstField(formContainerRef, {
    enabled: open,
    modal: true
  });
  const [classification, setClassification] = useState<
    Classifications | undefined
  >(undefined);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<ScopeStatus>("NOT_STARTED");
  /**
   * Selected designer. With a project catalog this is the catalog id;
   * without one it is the display name. Empty until the user picks one.
   */
  const [responsibility, setResponsibility] = useState<string | undefined>(
    undefined
  );
  const responsibilityEdited = useRef(false);
  const addedDesignerNames = useRef(new Map<string, string>());
  const responsibilitiesRef = useRef(responsibilities);
  responsibilitiesRef.current = responsibilities;
  const [notes, setNotes] = useState("");
  const [commitments, setCommitments] = useState<Commitment[]>([]);
  const [bids, setBids] = useState<BidAssociation[]>([]);
  const [showAllBps, setShowAllBps] = useState(false);

  // Reset while rendering, before the design-package editor applies a default
  // LOD. An effect reset runs too late: the editor can write the previous
  // row (including a chosen design package) back on top of the blank one.
  const formKey = `${open ? "open" : "closed"}:${initial?.id ?? ""}:${
    seed ? JSON.stringify(seed) : ""
  }`;
  const [appliedFormKey, setAppliedFormKey] = useState("");
  if (appliedFormKey !== formKey) {
    setAppliedFormKey(formKey);
    if (open) {
      setShowAllBps(false);
      setClassification(initial?.classification ?? seed?.classification);
      setTitle(initial?.title ?? "");
      setDescription(initial?.description ?? "");
      setStatus(initial?.status ?? "NOT_STARTED");
      responsibilityEdited.current = false;
      addedDesignerNames.current = new Map();
      const catalog = responsibilitiesRef.current;
      const savedName = initial?.responsibility?.trim();
      if (!savedName) {
        setResponsibility(undefined);
      } else if (!catalog) {
        setResponsibility(savedName);
      } else {
        setResponsibility(
          findResponsibilityOption(catalog, savedName)?.id ?? savedName
        );
      }
      setNotes(initial?.notes ?? "");
      // New items seed one blank commitment/bid row; `seed` can override those
      // when a caller pre-links a design package or bid package.
      setCommitments(
        initial?.commitments ?? seed?.commitments ?? [blankCommitment()]
      );
      setBids(
        initial?.bids ??
          seed?.bids ?? [{ bidPackageId: "", role: "", primary: false }]
      );
    }
  }

  // Once the project catalog arrives, match a saved name to its id.
  useEffect(() => {
    if (
      !open ||
      responsibilityEdited.current ||
      !responsibilities?.length ||
      !initial?.responsibility?.trim()
    ) {
      return;
    }
    const match = findResponsibilityOption(
      responsibilities,
      initial.responsibility
    );
    if (!match) return;
    setResponsibility((current) => (current === match.id ? current : match.id));
  }, [open, initial, responsibilities]);

  // Bid packages, narrowed to those linked to the design package(s) chosen in
  // the commitments — so a user isn't sifting the whole project's packages.
  const selectedDpIds = useMemo(
    () =>
      Array.from(
        new Set(commitments.map((c) => c.designPackageId).filter(Boolean))
      ),
    [commitments]
  );
  const { bpOptions, bpFilteredByDp } = useMemo(() => {
    const all = entities.bidPackages.map((b) => ({
      value: b.id,
      label: b.label
    }));
    if (showAllBps || !selectedDpIds.length) {
      return { bpOptions: all, bpFilteredByDp: false };
    }
    const linked = new Set(
      selectedDpIds.flatMap((dp) =>
        (entities.bidPackagesByDp[dp] ?? []).map((b) => b.id)
      )
    );
    if (!linked.size) return { bpOptions: all, bpFilteredByDp: false };
    // Keep any already-picked bid visible even if it isn't linked, so editing
    // an existing item never drops its bids from the picker.
    bids.forEach((b) => b.bidPackageId && linked.add(b.bidPackageId));
    return {
      bpOptions: all.filter((o) => linked.has(o.value)),
      bpFilteredByDp: true
    };
  }, [
    entities.bidPackages,
    entities.bidPackagesByDp,
    selectedDpIds,
    showAllBps,
    bids
  ]);

  const pendingCommitments = pendingDesignPackageCommitments(commitments);
  const depsReady =
    !entities.loading &&
    isDesignPackageCommitmentsReady(
      commitments,
      lodOptionsLoading,
      lodOptions.length
    );
  const trimmedTitle = title.trim();
  const titleTaken = useMemo(() => {
    if (!trimmedTitle) return false;
    const needle = trimmedTitle.toLowerCase();
    return existingTitles.some((row) => {
      if (row.id === initial?.id) return false;
      const existing = row.title?.trim();
      if (!existing) return false;
      return existing.toLowerCase() === needle;
    });
  }, [existingTitles, initial?.id, trimmedTitle]);
  const canSave = Boolean(trimmedTitle) && depsReady && !titleTaken;

  const [saving, setSaving] = useState(false);
  const save = async () => {
    // Rows still on their seeded/blank defaults (no DP or bid package picked)
    // are dropped rather than persisted, so an item saved without a
    // commitment/bid choice doesn't resolve to a phantom "—" row downstream.
    setSaving(true);
    try {
      const raw = (responsibility ?? "").trim();
      const byId = responsibilities?.find((option) => option.id === raw);
      const byName = responsibilities
        ? findResponsibilityOption(responsibilities, raw)
        : undefined;
      const selected = byId ?? byName;
      const addedName = addedDesignerNames.current.get(raw);
      const designer = selected
        ? {
            responsibility: selected.name,
            responsibleDesignerId: selected.id
          }
        : addedName
          ? { responsibility: addedName, responsibleDesignerId: raw }
          : responsibilities && !raw
            ? { responsibility: "", responsibleDesignerId: "" }
            : { responsibility: raw };
      await onSubmit({
        classification,
        title: title.trim(),
        description: description.trim(),
        status,
        ...designer,
        notes: notes.trim(),
        commitments: pendingCommitments,
        bids: pendingBidAssociations(bids)
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size={560}
      closable={{ placement: "end" }}
      mask={{ closable: false }}
      destroyOnHidden
      className="dsm-edit"
      title={initial ? "Edit Scope Item" : "Create Scope Item"}
      footer={
        <div className="flex justify-end gap-2">
          {initial && onDelete && (
            <Popconfirm
              title="Delete this scope item?"
              description="This can't be undone."
              okText="Delete"
              okButtonProps={{ danger: true, className: "ciq-delete-btn" }}
              onConfirm={onDelete}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                className="ciq-delete-btn mr-auto"
                data-testid="scope-modal-delete"
              >
                Delete
              </Button>
            </Popconfirm>
          )}
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            data-testid="scope-modal-save"
            disabled={!canSave || saving}
            loading={saving}
            onClick={() => void save()}
          >
            {initial ? "Save" : "Create Scope Item"}
          </Button>
        </div>
      }
    >
      <div ref={formContainerRef}>
        <Section title="Basic Details">
          <Field label="Title" required>
            <Input
              data-testid="scope-title"
              data-autofocus="true"
              placeholder="Name the scope item"
              value={title}
              status={titleTaken ? "error" : undefined}
              onChange={(e) => setTitle(e.target.value)}
            />
            {titleTaken && (
              <Typography.Text
                type="danger"
                style={{ fontSize: 12 }}
                data-testid="scope-title-error"
              >
                {ErrorMessages.ScopeItemTitleMsg}
              </Typography.Text>
            )}
          </Field>
          <Field label="Description">
            <Input.TextArea
              data-testid="scope-description"
              autoSize={{ minRows: 2 }}
              placeholder="Add what's included in scope"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Field
            label="Responsibility"
            info="Who owns delivering this scope item"
          >
            <ScopeResponsibilitySelect
              className="w-full"
              testId="scope-responsibility"
              value={responsibility ?? ""}
              catalog={responsibilities}
              catalogLoading={responsibilitiesLoading}
              onAdd={
                onAddResponsibility
                  ? async (name) => {
                      const result = await onAddResponsibility(name);
                      if (result.success && result.id && result.name) {
                        addedDesignerNames.current.set(result.id, result.name);
                      }
                      return result;
                    }
                  : undefined
              }
              onChange={(next) => {
                responsibilityEdited.current = true;
                setResponsibility(next || undefined);
              }}
            />
          </Field>
        </Section>

        <Section title="Classification Format">
          <ClassificationPicker
            value={classification}
            onChange={setClassification}
          />
        </Section>

        <ScopeDesignPackageCommitmentsEditor
          commitments={commitments}
          onCommitmentsChange={setCommitments}
          entities={entities}
          lodOptions={lodOptions}
          lodOptionsLoading={lodOptionsLoading}
          active={open}
        />

        <ScopeBidPackagesCardsEditor
          bids={bids}
          onBidsChange={setBids}
          bpOptions={bpOptions}
          selectPopupRender={(menu) => (
            <>
              {selectedDpIds.length > 0 && (
                <div className="flex items-center justify-between px-3 py-1">
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {bpFilteredByDp
                      ? "Displaying suggested Bid Packages"
                      : "Displaying all Bid Packages"}
                  </Typography.Text>
                  <Button
                    type="link"
                    size="small"
                    data-testid="bid-toggle-linked"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setShowAllBps((v) => !v)}
                  >
                    {showAllBps ? "Suggested only" : "Show all"}
                  </Button>
                </div>
              )}
              {menu}
            </>
          )}
        />
      </div>
    </Drawer>
  );
}
